import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Drawer, Button, Space, Spin, message } from 'antd';
import { SaveOutlined, CloseOutlined, RobotOutlined, MessageOutlined, ThunderboltOutlined, InfoCircleOutlined } from '@ant-design/icons';
import axios from 'axios';
import config from '../config';
import WorkflowChangesApproval from './WorkflowChangesApproval'

const WorkflowAssistant = ({ workflow, selectedNode, colorMode, onNodeSelect })=> {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('chat');
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [showModeMenu, setShowModeMenu] = useState(false);
  const messagesEndRef = useRef(null);
  const contextDebounceRef = useRef(null);
  const [workflowVersion, setWorkflowVersion] = useState(0);
  const [nodeNotification, setNodeNotification] = useState(null);
  const [isNodeMode, setIsNodeMode] = useState(false);
  const [requiresSave, setRequiresSave] = useState(false);
  const [showChatBubble, setShowChatBubble] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const lastWorkflowRef = useRef(null);
  const [isExistingSession, setIsExistingSession] = useState(false);

  const VALID_MODES = ['chat', 'assistant'];


  useEffect(() => {
    const timer = setTimeout(() => {
      setShowChatBubble(true);
    }, 10000);
  
    return () => clearTimeout(timer);
  }, []);

  // Initialize or resume session when drawer opens
  useEffect(() => {
    if (isOpen) {
      if (!workflow.isSaved) {
        setRequiresSave(true);
      } else if (!sessionId) {
        setRequiresSave(false);
        initializeSession();
      }
    }
  }, [isOpen, workflow.isSaved]);

  // Update context when workflow or selected node changes
  useEffect(() => {
    if (workflow && !pendingUpdate) {
        const workflowString = JSON.stringify(workflow);
        if (workflowString !== lastWorkflowRef.current) {
            lastWorkflowRef.current = workflowString;
            setWorkflowVersion(prev => prev + 1);
        }
    }
  }, [workflow, pendingUpdate]);
  
  useEffect(() => {
    if (sessionId && workflow) {
      if (contextDebounceRef.current) {
        clearTimeout(contextDebounceRef.current);
      }
      contextDebounceRef.current = setTimeout(() => {
        updateAssistantContext();
      }, 1000);
    }
    return () => {
      if (contextDebounceRef.current) {
        clearTimeout(contextDebounceRef.current);
      }
    };
  }, [workflow, selectedNode, sessionId, workflowVersion]);

  useEffect(() => {
    if (selectedNode) {
      setNodeNotification(selectedNode.data.nodeName || `${selectedNode.type} ${selectedNode.id}`);
      setIsNodeMode(true);
    } else {
      setNodeNotification(null);
      setIsNodeMode(false);
    }
  }, [selectedNode]);

  const SaveRequired = () => (
    <div className="save-required">
      <div className="save-message">
        <SaveOutlined className="save-icon" />
        <h3>Save Required</h3>
        <p>Please save your workflow to use the AI assistant.</p>
        <p className="tip">The assistant needs a saved workflow to provide accurate help and suggestions.</p>
      </div>
  
      <style jsx>{`
        .save-required {
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          text-align: center;
          color: ${colorMode === 'dark' ? '#bfbfbf' : '#666'};
        }
  
        .save-message {
          background: ${colorMode === 'dark' ? '#1f1f1f' : '#f5f5f5'};
          padding: 24px;
          border-radius: 12px;
          max-width: 300px;
        }
  
        .save-icon {
          font-size: 24px;
          color: ${colorMode === 'dark' ? '#177ddc' : '#1677ff'};
          margin-bottom: 12px;
        }
  
        h3 {
          margin: 0 0 12px;
          color: ${colorMode === 'dark' ? '#fff' : '#000'};
        }
  
        p {
          margin: 0 0 8px;
        }
  
        .tip {
          font-size: 13px;
          opacity: 0.8;
        }
      `}</style>
    </div>
  );

  const initializeSession = async () => {
    try {
      setIsLoading(true);
      
      // First, check if a session already exists for this workflow
      const checkResponse = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/assistant/get-session/${workflow.id}`,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );
      
      if (checkResponse.data.session_exists) {
        // Use existing session
        setSessionId(checkResponse.data.session_id);
        setIsExistingSession(true);
        
        // Load message history for the existing session
        await loadChatHistory(checkResponse.data.session_id);
      } else {
        // Create a new session if none exists
        const response = await axios.post(
          `${config.apiSettings.root}wp-ai-workflows/v1/assistant/session`,
          {
            workflow_id: workflow.id,
            workflow_context: workflow
          },
          {
            headers: {
              'X-WP-Nonce': config.apiSettings.nonce,
              'Content-Type': 'application/json'
            }
          }
        );
        setSessionId(response.data.session_id);
        setIsExistingSession(false);
      }
    } catch (error) {
      message.error('Failed to initialize assistant chat');
      console.error('Session initialization error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadChatHistory = async (sessionId) => {
    try {
      const response = await axios.get(
        `${config.apiSettings.root}wp-ai-workflows/v1/assistant/history/${sessionId}`,
        {
          headers: {
            'X-WP-Nonce': config.apiSettings.nonce
          }
        }
      );
      
      if (response.data.messages && response.data.messages.length > 0) {
        // Transform the messages to the format expected by your component
        const formattedMessages = response.data.messages.map(msg => ({
          role: msg.role,
          content: msg.content
        }));
        
        setMessages(formattedMessages);
      }
      
      // Also get the current mode
      if (response.data.mode) {
        setMode(response.data.mode);
      }
      
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

const handleModeChange = (newMode) => {
  if (!VALID_MODES.includes(newMode)) {
    console.error('Invalid mode:', newMode);
    return;
  }
  
  setMode(newMode);
  axios.post(
    `${config.apiSettings.root}wp-ai-workflows/v1/assistant/update-mode`,
    {
      session_id: sessionId,
      mode: newMode
    },
    {
      headers: {
        'X-WP-Nonce': config.apiSettings.nonce
      }
    }
  ).then(() => {
    // Optional: Show mode change confirmation
    message.success(`Switched to ${newMode} mode`);
  }).catch(error => {
    console.error('Failed to update mode:', error);
    message.error('Failed to switch modes');
  });
};

const clearNodeSelection = () => {
  setNodeNotification(null);
  setIsNodeMode(false);
  if (typeof onNodeSelect === 'function') {
    onNodeSelect(null);
  }
};

const getMessageStyles = (colorMode) => ({
  messageContainer: {
    marginBottom: '16px',
    display: 'flex',
    justifyContent: 'flex-start'
  },
  messageContent: {
    maxWidth: '80%',
    padding: '12px 16px',
    borderRadius: '12px',
    background: colorMode === 'dark' ? '#303030' : '#f0f0f0',
    color: colorMode === 'dark' ? '#fff' : '#000'
  },
  userMessage: {
    justifyContent: 'flex-end'
  },
  userMessageContent: {
    background: colorMode === 'dark' ? '#177ddc' : '#1677ff',
    color: '#fff',
    borderBottomRightRadius: '4px'
  },
  assistantMessageContent: {
    borderBottomLeftRadius: '4px'
  },
  text: {
    paragraph: {
      margin: '0.5em 0'
    },
    list: {
      margin: '0.5em 0',
      paddingLeft: '20px'
    },
    nestedList: {
      margin: '0.25em 0'
    },
    listItem: {
      margin: '0.25em 0'
    },
    strong: {
      color: colorMode === 'dark' ? '#fff' : '#000',
      fontWeight: 600
    },
    emphasis: {
      fontStyle: 'italic',
      opacity: 0.9
    },
    code: {
      background: colorMode === 'dark' ? '#1f1f1f' : '#f5f5f5',
      padding: '2px 4px',
      borderRadius: '4px',
      fontFamily: 'monospace',
      fontSize: '0.9em'
    },
    pre: {
      background: colorMode === 'dark' ? '#1f1f1f' : '#f5f5f5',
      padding: '12px',
      borderRadius: '6px',
      overflowX: 'auto',
      margin: '0.5em 0'
    }
  }
 });

const Message = React.memo(({ content, role, changes, onApprove, onReject, colorMode }) => {
  const styles = useMemo(() => getMessageStyles(colorMode), [colorMode]);
 
  // Handle workflow changes approval
  if (content === 'workflow-changes-approval' && changes) {
    return (
      <div className="message assistant" style={styles.messageContainer}>
        <WorkflowChangesApproval
          changes={changes}
          onApprove={onApprove}
          onReject={onReject}
          colorMode={colorMode}
        />
      </div>
    );
  }
 
  // Render regular message
  return (
    <div 
      className={`message ${role}`} 
      style={{
        ...styles.messageContainer,
        ...(role === 'user' && styles.userMessage)
      }}
    >
      <div 
        className="message-content"
        style={{
          ...styles.messageContent,
          ...(role === 'user' ? styles.userMessageContent : styles.assistantMessageContent)
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    </div>
  );
 }, (prevProps, nextProps) => {
  return prevProps.content === nextProps.content &&
         prevProps.role === nextProps.role &&
         prevProps.colorMode === nextProps.colorMode &&
         JSON.stringify(prevProps.changes) === JSON.stringify(nextProps.changes) &&
         prevProps.onApprove === nextProps.onApprove &&
         prevProps.onReject === nextProps.onReject;
 });
 
 Message.displayName = 'Message';

 

const EmptyState = React.memo(({ mode, modeExplanations, commonPrompts, onPromptClick }) => (
  <div className="empty-state">
    {modeExplanations[mode]}
    <div className="suggested-prompts">
      <h4>Try asking:</h4>
      {commonPrompts[mode].map((prompt, index) => (
        <div
          key={`prompt-${index}`}
          className="prompt-suggestion"
          onClick={() => onPromptClick(prompt)}
        >
          <span>→</span> {prompt}
        </div>
      ))}
    </div>
  </div>
));

const EmptyStateHint = () => (
  <div className="node-interaction-hint">
    <p>Ask questions about:</p>
    <ul>
      <li><span className="hint-bullet">•</span> Click any node to ask specific questions about it</li>
      <li><span className="hint-bullet">•</span> Or just type to ask about the whole workflow</li>
    </ul>
  </div>
);

const updateAssistantContext = async () => {
    try {
        await axios.post(
            `${config.apiSettings.root}wp-ai-workflows/v1/assistant/context`,
            {
                session_id: sessionId,
                workflow_context: workflow,
                selected_node: selectedNode?.id
            },
            {
                headers: {
                    'X-WP-Nonce': config.apiSettings.nonce,
                    'Content-Type': 'application/json'
                }
            }
        );
    } catch (error) {
        console.error('Context update error:', error);
    }
};

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!inputValue.trim() || isLoading || !sessionId) return;

  const messageContent = selectedNode 
    ? `[Regarding node ${selectedNode.id}]: ${inputValue}`
    : inputValue;

  setIsLoading(true);
  setMessages(prev => [...prev, {
    role: 'user',
    content: inputValue
  }]);
  setInputValue('');

  try {

    const response = await axios.post(
      `${config.apiSettings.root}wp-ai-workflows/v1/assistant/message`,
      {
        session_id: sessionId,
        content: messageContent,
        mode: mode
      },
      {
        headers: {
          'X-WP-Nonce': config.apiSettings.nonce,
          'Content-Type': 'application/json'
        }
      }
    );


    if (mode === 'assistant') {

      let suggestedChanges;
      if (typeof response.data.content === 'object') {
        suggestedChanges = response.data.content;
      } else {
        try {
          const jsonMatch = response.data.content.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            suggestedChanges = JSON.parse(jsonMatch[0]);
          } 
        } catch (error) {
          console.error('JSON parsing error:', error);
        }
      }


      // Validate suggestedChanges structure
      if (suggestedChanges?.changes) {
        
        // Ensure changes object has all required properties
        const changesObj = {
          modified: suggestedChanges.changes.modified || [],
          added: suggestedChanges.changes.added || [],
          removed: suggestedChanges.changes.removed || [],
          connections: suggestedChanges.changes.connections || []
        };

        // Check if any changes are actually present
        const hasActualChanges = 
          changesObj.modified.length > 0 ||
          changesObj.added.length > 0 ||
          changesObj.removed.length > 0 ||
          changesObj.connections.length > 0;

        if (hasActualChanges) {
          
          // Add explanation message first
          if (suggestedChanges.explanation) {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: suggestedChanges.explanation
            }]);
          }
        
          // Add approval component message
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: 'workflow-changes-approval',
            changes: changesObj,
            onApprove: async () => {
              try {
                setPendingUpdate(changesObj);
                
                const result = await axios.post(
                  `${config.apiSettings.root}wp-ai-workflows/v1/assistant/apply-changes`,
                  {
                    session_id: sessionId,
                    changes: changesObj
                  },
                  {
                    headers: {
                      'X-WP-Nonce': config.apiSettings.nonce
                    }
                  }
                );
                
                if (result.data.success && result.data.workflow) {
                  // Validate result structure
                  if (!result.data.workflow.nodes || !Array.isArray(result.data.workflow.nodes)) {
                    console.error('Invalid nodes data in response', result.data);
                    throw new Error('Invalid workflow data received from server');
                  }
                  
                  // Apply the changes to the UI
                  if (typeof workflow.onWorkflowUpdate === 'function') {
                    workflow.onWorkflowUpdate(result.data.workflow);
                  }
                  
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: 'Changes have been applied successfully!'
                  }]);
                } else {
                  throw new Error('Invalid response from server');
                }
              } catch (error) {
                console.error('Error applying changes:', error);
                message.error('Failed to apply workflow changes');
                
                setMessages(prev => [...prev, {
                  role: 'assistant',
                  content: `I encountered an error while applying the changes: ${error.message}`
                }]);
              } finally {
                setPendingUpdate(null);
              }
            },
            onReject: () => {
              
              setMessages(prev => [...prev, {
                role: 'assistant',
                content: 'Changes rejected. Would you like me to suggest different modifications instead?'
              }]);
          
              if (typeof workflow.onClearPendingChanges === 'function') {
                workflow.onClearPendingChanges();
              }
            }
          }]);
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: response.data.content
            }]);
          }
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: response.data.content
            }]);
          }
          } else {
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: response.data.content
            }]);
          }
          } catch (error) {
            console.error('Complete error details:', {
              error: error,
              message: error.message,
              response: error.response?.data,
              status: error.response?.status
            });
            
            message.error('Failed to send message');
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: 'Sorry, I encountered an error. Please try again.'
            }]);
          } finally {
            setIsLoading(false);
          }
          };



  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  

  const handlePromptClick = (prompt) => {
    setInputValue(prompt);
  };

  const commonPrompts = {
    chat: [
      "What do you think about my workflow?",
      "How can I make this workflow more efficient?",
      "What does this node type do?"
    ],
    assistant: [
      "Fine tune this AI Model parameters based on my usecase",
      "Optimize this workflow's performance",
      "Add human in the loop to the correct steps"
    ]
  };

  const modeExplanations = {
    chat: (
      <div className="mode-explanation">
        <h3>Chat Mode</h3>
        <p>Ask questions about your workflow and get instant help with:</p>
        <ul>
          <li>Understanding node functionality</li>
          <li>Improving prompts</li>
          <li>Best practices and tips</li>
        </ul>
      </div>
    ),
    assistant: (
      <div className="mode-explanation">
        <h3>Assistant Mode</h3>
        <p>
        I'll suggest changes to your nodes and workflow and wait for your approval before making them.
        </p>
      </div>
    )
  };

  const messagesContainer = useMemo(() => (
    <div className="messages-container">
      {messages.length === 0 ? (
        <EmptyState 
          mode={mode}
          modeExplanations={modeExplanations}
          commonPrompts={commonPrompts}
          onPromptClick={handlePromptClick}
        />
      ) : (
        messages.map((message, index) => (
          <Message 
            key={`message-${index}-${message.role}`}
            {...message}
            colorMode={colorMode}
          />
        ))
      )}
      {isLoading && (
        <div className="thinking-indicator">
          <span className="glowing-text">Thinking</span>
          <div className="dots">
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      )}
      <div ref={messagesEndRef} />
    </div>
  ), [messages, mode, isLoading, colorMode]); // Removed handlePromptClick

  return (
    <>
        {/* Chat Bubble */}
        {showChatBubble && (
        <div className="assistant-bubble">
        <div className="bubble-content">
          <RobotOutlined className="bubble-icon" />
          <span>Need help? I'm here to assist!</span>
          <Button 
            type="text" 
            size="small" 
            className="close-bubble" 
            onClick={() => setShowChatBubble(false)}
            icon={<CloseOutlined style={{ fontSize: '10px' }} />} 
          />
        </div>
        <div className="bubble-tail" />
        </div>
    )}
      {/* Assistant Button */}
      <div 
        style={{
          position: 'absolute',
          right: '20px',
          top: '50%',
          transform: 'translateY(calc(-50% - 120px))',
          zIndex: 5,
          background: colorMode === 'dark' ? '#141414' : 'white',
          padding: '8px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div className="assistant-button-wrapper">
          <Button
            type="text"
            onClick={() => setIsOpen(true)}
            className="assistant-button"
            icon={
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                viewBox="0 0 368.13 389.64"
                style={{
                  width: '20px',
                  height: '20px',
                  fill: 'currentColor'
                }}
              >
                <path d="M247.96,269.25h-.06s0,.05,0,.08c0-.03,0-.06,0-.08-32.84-.02-61,.03-66.53,.27-39.91,1.73-60.1-28.83-60.1-59.14,0-.09-31.12-.09-31.12,0-1.29,61.87-44.36,60.13-88.33,59.16-.17,13.74,0,29.77,0,29.77H31.35c30.77,0,58.97,24.06,59.64,59.44-.02,0-.02,30.88,0,30.88h31.15c2.43-27.34-14.19-86.53,58.46-90.32,25.55,0,47.92,0,67.44,0,0-.04,.03-25.78-.06-30.07Zm.06,30.07h30.47c-9.28,0-19.42,0-30.47,0h0ZM1.6,210.32H90.04s0-24.13,0-29.72c-45.06-.02-88.45-.05-88.45-.05v29.77Zm246.36,58.93c9.93,0,20.28,.02,30.56,.04,.79-35.25,29.15-58.97,59.86-58.97h29.54s.17-16.03,0-29.77c-9.81,0-26.1-.05-30.16-.07-30.84,0-58.66-15.27-58.66-60.45,0-.01,0-.02,0-.03h-31.27c0,.59,0,1.19,0,1.81,.71,34.3-19.64,58.71-59.45,58.71-5.57,.07-34.06,.09-67.18,.08-.1,12.83,0,29.88,0,29.71h67.95c56.55,0,58.57,46.67,58.75,58.93h0c.03-.84,.05-.76,.06,0Zm30.56,.04c0,.08,0,.16-.01,.23-.15,8.7-.02,29.78-.02,29.79,67.26,0,89.64,0,89.64,0v-29.77s-44.14-.17-89.61-.25ZM179.65,120h68.17c0-20.56,.09-18.94,.09-29.68h30.68s.33,12.73,.5,29.68h88.1v-29.77s-172.15,.15-186.77-.02c-39.82,0-60.17-24.41-59.45-58.71C120.97,9.01,120.87,11.05,120.87,0h-30.68s-.54,29.71-.5,29.71c0,45.18-27.82,60.45-58.66,60.45-4.05,.01-20.35,.07-30.16,.07-.17,13.74,0,29.77,0,29.77H30.42c30.77,0,58.97,24.06,59.64,59.44,0,0,0,.43,0,1.16,10.46,0,21.02,0,31.14,0,0-.44,0-.87,.01-1.29,.17-12.13,1.78-59.31,58.45-59.31Z" />
              </svg>
            }
          />
        </div>
      </div>

      {/* Assistant Drawer */}
      <Drawer
        title={
          <Space>
            <RobotOutlined />
            <span>Workflow Assistant</span>
          </Space>
        }
        placement="right"
        onClose={() => setIsOpen(false)}
        open={isOpen}
        width={400}
        mask={false}
        getContainer={() => document.querySelector('.reactflow-wrapper')}
        styles={{
          mask: {
            position: 'absolute'
          },
          wrapper: {
            position: 'absolute',
            height: 'calc(100% - 97px)',
            top: '97px',
            right: 0,
            zIndex: 999,
            boxShadow: '-2px 0 8px rgba(0,0,0,0.15)'
          },
          content: {
            height: '100%',
            position: 'relative'
          },
          body: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: 'calc(100% - 55px)',
            background: colorMode === 'dark' ? '#141414' : '#fff',
            overflow: 'hidden'
          }
        }}
      >
        {requiresSave ? (
          <SaveRequired />
        ) : (
          <>
         {messagesContainer}

            {/* Node Notification */}
            {nodeNotification && (
              <div className="node-notification">
                <div className="notification-content">
                  <RobotOutlined style={{ marginRight: '8px', color: colorMode === 'dark' ? '#177ddc' : '#1677ff' }}/>
                  <span>Asking about: {nodeNotification}</span>
                </div>
                <Button
                  type="text"
                  size="small"
                  className="close-button"
                  onClick={clearNodeSelection}
                  icon={<CloseOutlined style={{color: colorMode === 'dark' ? '#177ddc' : '#1677ff' }} />}
                />
              </div>
            )}

            {/* Empty State Hint */}
            {messages.length === 0 && !isLoading && <EmptyStateHint />}

            {/* Session Indicator - ADD HERE */}
            {isExistingSession && messages.length > 0 && (
              <div className="existing-session-indicator">
                <InfoCircleOutlined style={{ marginRight: '8px' }} />
                <span>Continuing previous conversation</span>
                <Button 
                  type="text" 
                  size="small" 
                  onClick={async () => {
                    setMessages([]);
                    setIsExistingSession(false);
                    
                    // Create a new session
                    const response = await axios.post(
                      `${config.apiSettings.root}wp-ai-workflows/v1/assistant/session`,
                      {
                        workflow_id: workflow.id,
                        workflow_context: workflow
                      },
                      {
                        headers: {
                          'X-WP-Nonce': config.apiSettings.nonce,
                          'Content-Type': 'application/json'
                        }
                      }
                    );
                    setSessionId(response.data.session_id);
                  }}
                >
                  Start New
                </Button>
              </div>
            )}

            {/* Mode Selection Menu */}
            {showModeMenu && (
              <div 
                className="mode-menu-overlay" 
                onClick={() => setShowModeMenu(false)}
              >
                <div 
                  className="mode-menu" 
                  onClick={(e) => e.stopPropagation()}
                >
                  <div 
                    className={`mode-option ${mode === 'chat' ? 'active' : ''}`}
                    onClick={() => {
                      handleModeChange('chat');  // Changed from setMode
                      setShowModeMenu(false);
                    }}
                  >
                    <div className="mode-option-header">
                      <MessageOutlined /> Chat Mode
                    </div>
                    <span className="mode-description">
                      Answers questions about your workflow, doesn't make changes
                    </span>
                  </div>
                  <div 
                    className={`mode-option ${mode === 'assistant' ? 'active' : ''}`}
                    onClick={() => {
                      handleModeChange('assistant');  // Changed from setMode
                      setShowModeMenu(false);
                    }}
                  >
                    <div className="mode-option-header">
                      <ThunderboltOutlined /> Assistant Mode
                    </div>
                    <span className="mode-description">
                      Makes changes to your workflow for you
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Input Container */}
            <div className="input-container">
              <form onSubmit={handleSubmit} className="input-wrapper">
                <div className="mode-selector-container">
                  <div 
                    className="mode-selector" 
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowModeMenu(!showModeMenu);
                    }}
                  >
                    {mode === 'chat' ? <MessageOutlined /> : <ThunderboltOutlined />}
                    <span>{mode === 'chat' ? 'Chat' : 'Assistant'}</span>
                  </div>
                </div>
                <input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={selectedNode 
                    ? `Ask me about the ${selectedNode.type} node...`
                    : mode === 'chat' 
                      ? "Ask me anything about your workflow" 
                      : "Tell me what to change"}
                  disabled={isLoading}
                />
              </form>

            </div>
          </>
        )}
      </Drawer>
      <style jsx>{`
        .assistant-button-wrapper {
          position: relative;
          padding: 0;
          border-radius: 4px;
        }

        .assistant-button {
          width: 32px !important;
          height: 32px !important;
          border-radius: 4px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          background: linear-gradient(
            45deg,
            #4285f4,
            #7b61ff,
            #2196f3,
            #9c27b0
          ) !important;
          background-size: 200% 200% !important;
          animation: gradientBorder 3s linear infinite !important;
          border: none !important;
          color: white !important;
        }

        .assistant-button:hover {
          opacity: 0.9 !important;
          transform: translateY(-1px) !important;
          transition: all 0.2s ease !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.2) !important;
        }

        @keyframes gradientBorder {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .messages-container {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          background: ${colorMode === 'dark' ? '#141414' : '#fff'};
        }

        .loading-indicator {
          display: flex;
          justify-content: center;
          padding: 12px;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          height: 100%;
          text-align: center;
          color: ${colorMode === 'dark' ? '#fff' : '#000'};
          padding: 20px;
        }

        .mode-explanation {
          margin-bottom: 32px;
          max-width: 300px;
        }

        .mode-explanation h3 {
          margin-bottom: 16px;
          font-size: 1.5em;
          font-weight: 600;
        }

        .mode-explanation ul {
          text-align: left;
          margin-top: 12px;
          padding-left: 20px;
        }

        .mode-explanation li {
          margin: 8px 0;
          color: ${colorMode === 'dark' ? '#999' : '#666'};
        }

        .suggested-prompts {
          width: 100%;
          max-width: 300px;
        }

        .prompt-suggestion {
          margin: 8px 0;
          padding: 12px 16px;
          background: ${colorMode === 'dark' ? '#1f1f1f' : '#f5f5f5'};
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s ease;
          text-align: left;
        }

        .prompt-suggestion:hover {
          background: ${colorMode === 'dark' ? '#2a2a2a' : '#e6e6e6'};
          transform: translateX(4px);
        }

        .prompt-suggestion span {
          color: ${colorMode === 'dark' ? '#177ddc' : '#1677ff'};
          margin-right: 8px;
        }

        .input-container {
          padding: 16px;
          border-top: 1px solid ${colorMode === 'dark' ? '#303030' : '#f0f0f0'};
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          background: ${colorMode === 'dark' ? '#1f1f1f' : '#f5f5f5'};
          border: 2px solid ${colorMode === 'dark' ? '#303030' : '#e6e6e6'};
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.2s ease;
        }

        .input-wrapper:focus-within {
          border-color: ${colorMode === 'dark' ? '#177ddc' : '#1677ff'};
          box-shadow: 0 0 0 2px ${colorMode === 'dark' ? 'rgba(23,125,220,0.2)' : 'rgba(22,119,255,0.2)'};
        }

        .input-wrapper input {
          flex: 1;
          border: none;
          background: transparent;
          padding: 12px;
          font-size: 14px;
          color: ${colorMode === 'dark' ? '#fff' : '#000'};
          outline: none;
        }

        .input-wrapper input::placeholder {
          color: ${colorMode === 'dark' ? '#666' : '#999'};
        }

        .input-wrapper input:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }

        .message {
          margin-bottom: 16px;
          display: flex;
          justify-content: flex-start;
        }

        .message.user {
          justify-content: flex-end;
        }

        .message-content {
          max-width: 80%;
          padding: 12px 16px;
          border-radius: 12px;
          background: ${colorMode === 'dark' ? '#303030' : '#f0f0f0'};
          color: ${colorMode === 'dark' ? '#fff' : '#000'};
        }

        .message.user .message-content {
          background: ${colorMode === 'dark' ? '#177ddc' : '#1677ff'};
          color: #fff;
          border-bottom-right-radius: 4px;
        }

        .message.assistant .message-content {
          border-bottom-left-radius: 4px;
        }

        .mode-selector-container {
          position: relative;
          margin-left: 12px;
        }

      .mode-selector {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 10px;
        background: ${colorMode === 'dark' ? '#141414' : '#f0f0f0'};
        border-radius: 6px;
        cursor: pointer;
        transition: all 0.2s ease;
        user-select: none;
        color: ${colorMode === 'dark' ? '#666' : '#666'};
        border: 1px solid #666;
      }

        .mode-selector:hover {
          background: ${colorMode === 'dark' ? '#1f1f1f' : '#e6e6e6'};
          color: ${colorMode === 'dark' ? '#177ddc' : '#1677ff'};
        }

        .mode-selector span {
          font-size: 13px;
        }

        .mode-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 10000;
        }

        .mode-menu {
          position: absolute;
          bottom: 80px;
          right: 20px;
          background: ${colorMode === 'dark' ? '#1f1f1f' : '#fff'};
          border: 1px solid ${colorMode === 'dark' ? '#303030' : '#e6e6e6'};
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          width: 360px;
          animation: slideUp 0.2s ease;
        }

        @keyframes slideUp {
          from {
            transform: translateY(10px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        .mode-option {
          padding: 16px;
          display: flex;
          flex-direction: column;
          gap: 4px;
          transition: all 0.2s ease;
          border-bottom: 1px solid ${colorMode === 'dark' ? '#303030' : '#e6e6e6'};
          cursor: pointer;
        }

        .mode-option:last-child {
          border-bottom: none;
        }

        .mode-option:hover {
          background: ${colorMode === 'dark' ? '#141414' : '#f5f5f5'};
        }

        .mode-option.active {
          color: ${colorMode === 'dark' ? '#177ddc' : '#1677ff'};
          background: ${colorMode === 'dark' ? '#111' : '#e6f4ff'};
        }

        .mode-option-header {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 500;
        }

        .mode-description {
          font-size: 12px;
          color: ${colorMode === 'dark' ? '#666' : '#999'};
        }

      .node-interaction-hint {
        padding: 24px;
        text-align: center;
        color: ${colorMode === 'dark' ? '#999' : '#666'};
      }

      .node-interaction-hint ul {
        list-style: none;
        padding: 0;
        margin: 12px 0 0;
      }

      .node-interaction-hint li {
        margin: 8px 0;
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .hint-bullet {
        color: ${colorMode === 'dark' ? '#177ddc' : '#1677ff'};
        font-size: 18px;
      }

      .node-notification {
        padding: 8px 16px;
        background: ${colorMode === 'dark' ? '#1f1f1f' : '#f0f0f0'};
        border-top: 1px solid ${colorMode === 'dark' ? '#303030' : '#e6e6e6'};
        border-bottom: 1px solid ${colorMode === 'dark' ? '#303030' : '#e6e6e6'};
        display: flex;
        align-items: center;
        justify-content: space-between;
        font-size: 13px;
      }

      .notification-content {
        display: flex;
        align-items: center;
        color: ${colorMode === 'dark' ? '#bfbfbf' : '#666'};
      }

      .close-button {
        opacity: 0.6;
        transition: opacity 0.2s ease;
      }

      .close-button:hover {
        opacity: 1;
      }

      .existing-session-indicator {
      background: ${colorMode === 'dark' ? '#1f1f1f' : '#f0f7ff'};
      color: ${colorMode === 'dark' ? '#bfbfbf' : '#666'};
      padding: 8px 16px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 13px;
      margin-bottom: 12px;
      border-radius: 4px;
    }

      .notification-content span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .thinking-indicator {
        padding: 12px 16px;
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .glowing-text {
        font-size: 14px;
        color: ${colorMode === 'dark' ? '#666' : '#999'};
        position: relative;
        overflow: hidden;
      }

      .glowing-text::after {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(
          90deg,
          transparent,
          ${colorMode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.8)'},
          transparent
        );
        animation: glare 2s infinite;
      }

      .dots {
        display: flex;
        gap: 2px;
        margin-left: 2px;
      }

      .dots span {
        width: 4px;
        height: 4px;
        background: ${colorMode === 'dark' ? '#666' : '#999'};
        border-radius: 50%;
        animation: typing 1.4s infinite ease-in-out;
      }

      .dots span:nth-child(1) { animation-delay: 0s; }
      .dots span:nth-child(2) { animation-delay: 0.2s; }
      .dots span:nth-child(3) { animation-delay: 0.4s; }

      @keyframes typing {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-4px); }
      }

      @keyframes glare {
        0% { left: -100%; }
        100% { left: 200%; }
      }

      .assistant-bubble {
        position: absolute;
        right: 70px;
        top: 50%;
        transform: translateY(calc(-50% - 120px));
        z-index: 5;
        max-width: 220px;
      }

      .bubble-content {
        background: ${colorMode === 'dark' ? '#1f1f1f' : '#ffffff'};
        color: ${colorMode === 'dark' ? '#ffffff' : '#000000'};
        padding: 12px 16px;
        border-radius: 16px;
        font-size: 13px;
        display: flex;
        align-items: center;
        gap: 8px;
        box-shadow: ${colorMode === 'dark' ? 
          '0 4px 24px rgba(0, 0, 0, 0.4)' : 
          '0 2px 12px rgba(0, 0, 0, 0.08)'};
        animation: bubbleIn 0.3s ease;
        border: 1px solid ${colorMode === 'dark' ? '#333' : '#e1e1e1'};
      }

      .bubble-tail {
        position: absolute;
        right: -6px;
        top: 50%;
        transform: translateY(-50%) rotate(45deg);
        width: 12px;
        height: 12px;
        background: ${colorMode === 'dark' ? '#1f1f1f' : '#ffffff'};
        border-right: 1px solid ${colorMode === 'dark' ? '#333' : '#e1e1e1'};
        border-top: 1px solid ${colorMode === 'dark' ? '#333' : '#e1e1e1'};
      }

      .bubble-icon {
        font-size: 16px;
        color: ${colorMode === 'dark' ? '#177ddc' : '#1677ff'};
      }

      :global(.close-bubble) {
        position: absolute;
        top: -6px;
        right: -6px;
        width: 16px;
        height: 16px;
        min-width: 16px;
        border-radius: 50%;
        background: ${colorMode === 'dark' ? '#1f1f1f' : '#ffffff'};
        color: ${colorMode === 'dark' ? '#999' : '#666'};
        display: flex;
        align-items: center;
        justify-content: center;
        border: 1px solid ${colorMode === 'dark' ? '#333' : '#e1e1e1'};
        padding: 0;
        opacity: 0.8;
        transition: all 0.2s ease;
        box-shadow: ${colorMode === 'dark' ? 
          '0 2px 8px rgba(0, 0, 0, 0.3)' : 
          '0 1px 4px rgba(0, 0, 0, 0.05)'};
      }

      :global(.close-bubble:hover) {
        opacity: 1;
        transform: scale(1.05);
      }

      @keyframes bubbleIn {
        from {
          opacity: 0;
          transform: translateX(10px);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      `}</style>
    </>
  );
};

export default React.memo(WorkflowAssistant);